import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Package, ShoppingCart, CheckCircle2, AlertTriangle, PackageX, Boxes } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const DEFAULT_THRESHOLD = 15;

// Customer-facing availability (never exposes thresholds or internal counts).
const availability = (p) => {
  const avail = p?.availability || ((p?.stock_quantity ?? 0) > 0 ? 'available' : 'out_of_stock');
  if (avail === 'out_of_stock') return { key: 'out', label: 'Out of Stock', color: '#EF4444', icon: PackageX };
  return { key: 'in', label: 'Available', color: '#10B981', icon: CheckCircle2 };
};

/**
 * Customer-facing (dealer / elite_partner) and sales-rep read-only catalog.
 * Shows the viewer's AUTHORIZED effective price from the pricing engine and
 * customer-facing availability language. No admin controls, thresholds, base
 * pricing or inventory activity are exposed.
 */
export const ProductCatalog = ({ user, setActiveTab }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [availFilter, setAvailFilter] = useState('all');

  const isDealer = user?.role === 'installation_center' || user?.role === 'elite_partner';
  const isElite = user?.role === 'elite_partner';

  useEffect(() => {
    (async () => {
      try {
        const [pr, cr] = await Promise.all([
          axios.get(`${API}/api/products?limit=500`),
          axios.get(`${API}/api/categories`),
        ]);
        setProducts(pr.data.products || pr.data || []);
        setCategories(cr.data || []);
      } catch (e) { toast.error('Error loading products'); }
      finally { setLoading(false); }
    })();
  }, []);

  const catName = (name) => categories.find((c) => c.name === name)?.display_name || name;
  // Authorized effective price for this viewer (engine already scoped it).
  const myPrice = (p) => (typeof p.discounted_price === 'number' ? p.discounted_price : p.price);

  const visible = useMemo(() => {
    let list = [...products];
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter((p) =>
      [p.name, catName(p.category), p.size, p.finish].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)));
    if (catFilter !== 'all') list = list.filter((p) => p.category === catFilter);
    if (availFilter !== 'all') list = list.filter((p) => availability(p).key === availFilter);
    return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [products, q, catFilter, availFilter, categories]);

  return (
    <div className="space-y-6" data-testid="product-catalog">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Products</h2>
          <p className="text-slate-400 text-sm mt-1">Browse the TC catalog{isDealer ? ' with your authorized pricing' : ''}.</p>
        </div>
        {isDealer && (
          <button onClick={() => setActiveTab && setActiveTab('create-order')} data-testid="catalog-create-order-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium hover:bg-[#d4b36e] transition-colors">
            <ShoppingCart className="w-4 h-4" /> Create Order
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products..."
            className={`${inputCls} pl-9`} data-testid="catalog-search-input" />
        </div>
        <div className="flex gap-3">
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="catalog-category-filter">
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c.name} value={c.name}>{c.display_name}</option>)}
          </select>
          <select value={availFilter} onChange={(e) => setAvailFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="catalog-availability-filter">
            <option value="all">All Availability</option>
            <option value="in">Available</option>
            <option value="out">Out of Stock</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="animate-pulse bg-white/[0.05] rounded-2xl h-48" />)}
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-16 text-center">
          <Boxes className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No products found</p>
          <p className="text-slate-500 text-sm mt-1">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((p) => {
            const av = availability(p);
            const AvIcon = av.icon;
            return (
              <div key={p.product_id} data-testid={`catalog-product-${p.product_id}`}
                className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5 flex flex-col hover:border-[#C6A15B]/30 transition-colors">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <h3 className="text-slate-100 font-semibold leading-snug">{p.name}</h3>
                    <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#C6A15B]/15 text-[#C6A15B]">{catName(p.category)}</span>
                  </div>
                  <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
                    style={{ background: `${av.color}1f`, color: av.color }} data-testid="catalog-availability-badge">
                    <AvIcon className="w-3 h-3" /> {av.label}
                  </span>
                </div>

                {p.description && <p className="text-slate-500 text-xs mt-3 line-clamp-2">{p.description}</p>}

                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <div><span className="text-slate-500 text-xs">Size</span><p className="text-slate-200">{p.size || '—'}</p></div>
                  {p.finish && <div><span className="text-slate-500 text-xs">Finish</span><p className="text-slate-200 capitalize">{p.finish}</p></div>}
                </div>

                <div className="mt-auto pt-4 flex items-end justify-between">
                  <div>
                    <div className="text-slate-500 text-xs">{isDealer ? 'Your Price' : 'Price'}</div>
                    <div className="text-2xl font-bold text-[#C6A15B]" data-testid="catalog-price">{money(myPrice(p))}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductCatalog;
